import React, { useRef, useCallback, useState, useEffect } from 'react';
import { useEditing } from '@/state/EditingContext';
import { clipFadeLengths } from '@/audio/clipEdits';
import { dragBar } from '@/state/editingGrid';
import { GRID } from '@/utils/constants';
import { snapToGrid } from '@/state/useSnapGrid';
import { bpmsCompatible, bufferNativeBars, clipSpeed, effectiveSourceBpm, isValidBpm, needsStretch, timelineBars } from '@/audio/clipTiming';
import { isRenderedClip } from '@/audio/clipKinds';
import { clipPitch } from '@/audio/stretchedBuffers';
import { trimClip } from '@/state/clipTrim';
import { getCached } from '@/audio/BufferCache';
import { PatternClipPreview } from './PatternClipPreview';

const SPEEDS = [
  { id: 'half', value: 0.5, label: '\u00bd\u00d7', title: 'Half time: plays twice as long' },
  { id: 'normal', value: 1, label: '1\u00d7', title: 'Normal speed' },
  { id: 'double', value: 2, label: '2\u00d7', title: 'Double time: plays twice as fast' },
];
const pitchLabel = (pitch) => (pitch > 0 ? `+${pitch}` : pitch < 0 ? `\u2212${Math.abs(pitch)}` : '0');
export const colorWithAlpha = (color, hexAlpha, percent) => String(color).trim().startsWith('var(')
  ? `color-mix(in srgb, ${color} ${percent}%, transparent)`
  : `${color}${hexAlpha}`;
export const clipBackground = (color, classMuted = false) => classMuted
  ? `linear-gradient(135deg, color-mix(in srgb, ${color} 35%, var(--ba-surface-2)), color-mix(in srgb, ${color} 20%, var(--ba-surface-1)))`
  : `linear-gradient(135deg, ${colorWithAlpha(color, 'CC', 80)}, ${colorWithAlpha(color, '88', 53)})`;

export const Clip = ({
  clip,
  trackId,
  activeTool,
  isSelected,
  onSelect,
  onMove,
  onSlice,
  onDuplicate,
  onDelete,
  onTrim,
  onReset,
  onLoop,
  onEditPattern,
  onMakeUnique,
  onClipSound,
  kiosk = false,
  sessionBpm,
  pxPerBar = GRID.PX_PER_BAR,
  audioEligible = true,
  classMuted = false,
  allowClipSound = true,
  highlighted = false,
  maxEndBar = Infinity,
}) => {
  const editing = useEditing();
  const [fadePreview,setFadePreview] = useState(null);
  const [fadeUnit,setFadeUnit] = useState('beats');
  const color = clip.color;
  const isLibraryLoop = !isRenderedClip(clip);
  const pitch = clipPitch(clip);
  const offTempo = isLibraryLoop && isValidBpm(clip.sourceBpm) && !bpmsCompatible(clip.sourceBpm, sessionBpm);
  const stretched = needsStretch(clip, sessionBpm);
  const speed = clipSpeed(clip);
  const [ctxMenu, setCtxMenu] = useState(null);
  const [trimPreview, setTrimPreview] = useState(null);
  const didDragRef = useRef(false);

  const [dragPreview, setDragPreview] = useState(null);
  const dragCleanupRef = useRef(null);
  useEffect(() => () => dragCleanupRef.current?.(false), []);

  const handlePointerDown = useCallback((e) => {
    if (e.button !== 0) return;
    didDragRef.current = false;
    if (activeTool !== 'Select') return;
    e.preventDefault(); e.stopPropagation();
    dragCleanupRef.current?.();
    didDragRef.current = false;
    const handle = e.currentTarget, pointerId = e.pointerId;
    const startX = e.clientX, startBar = clip.start_bar;
    const scroller = handle.closest('[data-timeline-scroll]');
    const startScroll = scroller?.scrollLeft || 0;
    const maxStart = Math.max(0, maxEndBar - timelineBars(clip, sessionBpm));
    let proposed = startBar, cancelled = false;
    handle.setPointerCapture?.(pointerId);
    const cleanup = (reset = true) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('keydown', key);
      if (handle.hasPointerCapture?.(pointerId)) handle.releasePointerCapture?.(pointerId);
      dragCleanupRef.current = null;
      if (reset) setDragPreview(null);
    };
    const move = event => {
      if (event.pointerId !== pointerId || cancelled) return;
      const dx = event.clientX - startX + (scroller?.scrollLeft || 0) - startScroll;
      if (!didDragRef.current && Math.abs(dx) < 4) return;
      if (!didDragRef.current) { didDragRef.current = true; if (!isSelected) onSelect?.(clip.id, false); }
      proposed = dragBar(startBar, dx, pxPerBar, event.shiftKey, maxStart, editing.snap ?? null);
      setDragPreview({ ...clip, start_bar: proposed });
    };
    const up = event => {
      if (event.pointerId !== pointerId) return;
      move(event);
      cleanup();
      if (!cancelled && didDragRef.current && proposed !== startBar) onMove?.(clip.id, proposed);
    };
    const cancel = () => { cancelled = true; cleanup(); };
    const key = event => { if (event.key === 'Escape') { event.stopPropagation(); cancel(); } };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('keydown', key);
    dragCleanupRef.current = cleanup;
  }, [activeTool, clip, isSelected, maxEndBar, onMove, onSelect, pxPerBar, sessionBpm, editing.snap]);

  // Click: fires AFTER mousedown+mouseup. Only handle selection here.
  const handleClick = useCallback((e) => {
    e.stopPropagation();

    // If we just dragged, don't change selection
    if (didDragRef.current) return;

    if (activeTool === 'Select') {
      onSelect?.(clip.id, e.shiftKey);
    } else if (activeTool === 'Slice') {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const barWithinClip = x / pxPerBar;
      const sliceBar = snapToGrid(clip.start_bar + barWithinClip);
      onSlice?.(clip.id, sliceBar);
    } else if (activeTool === 'Copy') {
      onDuplicate?.(clip.id);
    }
  }, [activeTool, clip.id, clip.start_bar, onSelect, onSlice, onDuplicate, pxPerBar]);

  // Right-click context menu
  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // Select this clip on right-click (single select)
    onSelect?.(clip.id, false);
    setCtxMenu({ x: e.clientX, y: e.clientY });
  }, [clip.id, onSelect]);

  const closeCtxMenu = useCallback(() => setCtxMenu(null), []);

  const availableNative = useCallback(() => {
    if (clip.isPattern) return Number(clip.pattern?.patternBars || clip.naturalLoopBars || 1);
    const buffer = audioEligible ? getCached(clip.loop_id) : null;
    // The cached buffer is the original audio, so measure it at its own tempo (Knock clips: the song tempo).
    return buffer ? bufferNativeBars(clip, buffer, sessionBpm) : (clip.naturalLoopBars ?? ((clip.offset || 0) + clip.bars));
  }, [clip, sessionBpm, audioEligible]);

  const startTrim = useCallback((e, edge) => {
    if (activeTool !== 'Select' || e.button !== 0) return;
    e.preventDefault(); e.stopPropagation();
    const handle = e.currentTarget;
    const pointerId = e.pointerId;
    handle.setPointerCapture?.(pointerId);
    const original = clip;
    const lane = handle.closest(`[data-testid="clip-${clip.id}"]`).parentElement;
    const targetBar = (event) => (event.clientX - lane.getBoundingClientRect().left) / pxPerBar;
    let cancelled = false;
    const cleanup = () => {
      handle.onpointermove = null; handle.onpointerup = null; handle.onpointercancel = null;
      window.removeEventListener('keydown', onKey);
      handle.releasePointerCapture?.(pointerId);
      setTrimPreview(null);
    };
    const onKey = (event) => { if (event.key === 'Escape') { event.stopPropagation(); cancelled = true; cleanup(); } };
    handle.onpointermove = (event) => {
      if (cancelled) return;
      setTrimPreview(trimClip(original, edge, targetBar(event), { bpm: sessionBpm, availableNative: availableNative(), fine: event.shiftKey, kiosk, pxPerBar }));
    };
    handle.onpointerup = (event) => {
      if (!cancelled) onTrim?.(clip.id, edge, targetBar(event), event.shiftKey && !kiosk);
      cleanup();
    };
    handle.onpointercancel = cleanup;
    window.addEventListener('keydown', onKey);
  }, [activeTool, clip, onTrim, pxPerBar, sessionBpm, availableNative, kiosk]);

  const display = fadePreview || trimPreview || dragPreview || clip;
  const displayLeft = display.start_bar * pxPerBar;
  const displayWidth = timelineBars(display, sessionBpm) * pxPerBar;
  const beats = Math.round(timelineBars(display, sessionBpm) * 4);
  const lengthLabel = beats < 4 ? `${beats} beat${beats === 1 ? '' : 's'}` : `${Math.floor(beats / 4)} bar${Math.floor(beats / 4) === 1 ? '' : 's'}${beats % 4 ? ` ${beats % 4} beat${beats % 4 === 1 ? '' : 's'}` : ''}`;

  const clipSeconds = timelineBars(clip, sessionBpm)*240/sessionBpm;
  const envelopeUnit=240/effectiveSourceBpm(display,sessionBpm);
  const envelopeDuration=(display.fadeEnvelope?.bars??display.bars)*envelopeUnit;
  const envelopeOffset=(display.fadeEnvelope?.offsetBars||0)*envelopeUnit;
  const sourceFades=clipFadeLengths(display,envelopeDuration);
  const fades={fadeIn:Math.max(0,Math.min(clipSeconds,sourceFades.fadeIn-envelopeOffset)),fadeOut:Math.max(0,Math.min(clipSeconds,envelopeOffset+clipSeconds-(envelopeDuration-sourceFades.fadeOut)))};
  const startFadeHeight=sourceFades.fadeIn?52*(1-Math.min(1,envelopeOffset/sourceFades.fadeIn)):0;
  const endFadeHeight=sourceFades.fadeOut?52*(1-Math.max(0,Math.min(1,(envelopeDuration-envelopeOffset-clipSeconds)/sourceFades.fadeOut))):0;
  const startFade=(event,side)=>{
    if(event.button!==0)return;
    event.preventDefault();event.stopPropagation();
    const element=event.currentTarget,pointerId=event.pointerId,startX=event.clientX;
    element.setPointerCapture?.(pointerId);
    const initial=side==='in'?fades.fadeIn:fades.fadeOut;
    let value=initial;
    const move=e=>{if(e.pointerId!==pointerId)return;value=Math.max(0,Math.min(clipSeconds,initial+(e.clientX-startX)*(side==='in'?1:-1)/pxPerBar*240/sessionBpm));setFadePreview({...clip,[side==='in'?'fade_in':'fade_out']:value});};
    const clean=()=>{window.removeEventListener('pointermove',move);window.removeEventListener('pointerup',up);window.removeEventListener('pointercancel',cancel);window.removeEventListener('keydown',key);if(element.hasPointerCapture?.(pointerId))element.releasePointerCapture(pointerId);setFadePreview(null);dragCleanupRef.current=null;};
    const up=e=>{if(e.pointerId!==pointerId)return;move(e);clean();didDragRef.current=true;editing.onClipEdit?.(clip.id,{[side==='in'?'fade_in':'fade_out']:value});};
    const cancel=()=>{clean();didDragRef.current=true;};
    const key=e=>{if(e.key==='Escape')cancel();};
    window.addEventListener('pointermove',move);window.addEventListener('pointerup',up);window.addEventListener('pointercancel',cancel);window.addEventListener('keydown',key);dragCleanupRef.current=clean;
  };

  const topItems = [
    ...(!clip.isPattern && editing.onClipEdit ? [{ label: clip.reverse ? 'Play forward' : 'Reverse', action: () => { editing.onClipEdit(clip.id,{reverse:!clip.reverse}); closeCtxMenu(); }, testId:'ctx-reverse' }] : []),
    { label: 'Duplicate', action: () => { onDuplicate?.(clip.id); closeCtxMenu(); }, testId: 'ctx-duplicate' },
    { label: 'Loop this', action: () => { onLoop?.(clip.id); closeCtxMenu(); }, testId: 'ctx-loop-this' },
    ...((clip.isPattern || clip.isKnockEngine) ? [
      { label: clip.isKnockEngine ? 'Edit bass' : 'Edit pattern', action: () => { onEditPattern?.(clip.id); closeCtxMenu(); }, testId: 'ctx-edit-pattern' },
      { label: 'Make unique', action: () => { onMakeUnique?.(clip.id); closeCtxMenu(); }, testId: 'ctx-make-unique' },
    ] : []),
  ];
  const bottomItems = [
    ...(allowClipSound && isLibraryLoop && offTempo ? [{ label: `${clip.stretch === false ? '' : '\u2713 '}Stretch to song tempo`, action: () => { onClipSound?.(clip.id, { stretch: clip.stretch === false }); closeCtxMenu(); }, testId: 'ctx-stretch' }] : []),
    ...((clip.offset || 0) > 0 || (clip.naturalLoopBars != null && clip.bars < clip.naturalLoopBars) ? [{ label: 'Reset length', action: () => { onReset?.(clip.id); closeCtxMenu(); }, testId: 'ctx-reset-length' }] : []),
    { label: 'Delete', action: () => { onDelete?.(clip.id); closeCtxMenu(); }, testId: 'ctx-delete', danger: true },
  ];
  const renderItem = (item) => (
    <button
      key={item.testId}
      data-testid={item.testId}
      onClick={item.action}
      className="w-full text-left px-3 py-1.5"
      style={{ fontSize: 12, fontWeight: 500, background: 'transparent', color: item.danger ? 'var(--pink)' : 'var(--text)', border: 'none', cursor: 'pointer', display: 'block' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-elevated)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      {item.label}
    </button>
  );

  return (
    <>
      <div
        data-clip-id={clip.id} data-testid={`clip-${clip.id}`}
        className={`ba-timeline-clip absolute select-none overflow-hidden${highlighted ? ' class-highlight' : ''}`}
        style={{
          left: displayLeft,
          width: displayWidth,
          touchAction: activeTool === 'Select' ? 'none' : 'auto',
          top: 6,
          height: GRID.CLIP_HEIGHT,
          borderRadius: 6,
          background: clipBackground(color, classMuted),
          opacity: classMuted ? 0.35 : undefined,
          border: `2px solid ${isSelected ? '#fff' : color}`,
          boxShadow: isSelected ? `0 0 0 1px var(--green), 0 2px 8px ${colorWithAlpha(color, '55', 33)}` : `0 2px 8px ${colorWithAlpha(color, '33', 20)}`,
          zIndex: isSelected ? 3 : 2,
          cursor: dragPreview ? 'grabbing' : activeTool === 'Select' ? 'grab' : activeTool === 'Slice' ? 'crosshair' : 'copy',
        }}
        onPointerDown={handlePointerDown}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        onDoubleClick={(event) => { if (activeTool !== 'Select') { event.preventDefault(); return; } if (clip.isPattern || clip.isKnockEngine) onEditPattern?.(clip.id); }}
      >
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: `repeating-linear-gradient(90deg, transparent, transparent 3px, ${colorWithAlpha(color, '44', 27)} 3px, ${colorWithAlpha(color, '44', 27)} 4px)` }}
        />
        <span
          className="absolute px-2 py-1 truncate"
          style={{ fontSize: 10, fontWeight: 600, color: 'var(--text)', textShadow: '0 1px 3px rgba(0,0,0,0.6)', maxWidth: '100%' }}
        >
          {clip.name}
        </span>
        {clip.isPattern && <PatternClipPreview clip={display} />}
        {(pitch !== 0 || offTempo || speed !== 1) && <span className="clip-sound-badges" data-testid={`clip-badges-${clip.id}`}>
          {pitch !== 0 && <b title={`${pitchLabel(pitch)} semitones`}>{pitchLabel(pitch)}</b>}
          {speed !== 1 && <b title={speed < 1 ? 'Half time' : 'Double time'}>{speed < 1 ? '\u00bd\u00d7' : '2\u00d7'}</b>}
          {stretched && <b title={`Stretched from ${Math.round(clip.sourceBpm)} to ${Math.round(sessionBpm)} BPM`}>{Math.round(clip.sourceBpm)}{'\u2192'}{Math.round(sessionBpm)}</b>}
          {offTempo && !stretched && <b className="is-unstretched" title={`Plays at its own ${Math.round(clip.sourceBpm)} BPM (stretch is off)`}>{Math.round(clip.sourceBpm)} BPM</b>}
        </span>}
        {!clip.isPattern && <>
          {clip.reverse && <span className="clip-reverse-label">Reversed</span>}
          <svg className="clip-fade-curves" width="100%" height="100%" aria-hidden="true">
            {fades.fadeIn>0 && <path d={`M 0 0 L ${fades.fadeIn/clipSeconds*displayWidth} 0 L 0 ${startFadeHeight} Z`} />}
            {fades.fadeOut>0 && <path d={`M ${displayWidth} 0 L ${displayWidth-fades.fadeOut/clipSeconds*displayWidth} 0 L ${displayWidth} ${endFadeHeight} Z`} />}
          </svg>
          {activeTool==='Select' && editing.onClipEdit && ['in','out'].map(side=><button key={side} type="button" className="clip-fade-handle" data-testid={`fade-${side}-${clip.id}`} aria-label={`Fade ${side}`} title={`Drag for fade ${side}`}
            style={{[side==='in'?'left':'right']:Math.max(0,(side==='in'?fades.fadeIn:fades.fadeOut)/clipSeconds*displayWidth-5)}} onPointerDown={e=>startFade(e,side)} onClick={e=>e.stopPropagation()} />)}
        </>}
        {activeTool === 'Select' && ['left', 'right'].map((edge) => (
          <div key={edge} data-testid={`trim-${edge}-${clip.id}`} onPointerDown={(e) => startTrim(e, edge)}
            title={edge === 'left' ? 'Trim start' : 'Trim end'} className="clip-trim-handle absolute top-0 h-full" style={{ [edge]: 0, width: kiosk ? 14 : 10, cursor: 'ew-resize', zIndex: 4 }} />
        ))}
        {dragPreview && <span className="clip-drag-position" role="status">Bar {Number((dragPreview.start_bar + 1).toFixed(2))} · {lengthLabel}</span>}
        {trimPreview && <>
          <span className="absolute right-1 bottom-0.5" style={{ fontSize: 10, fontWeight: 700 }}>{lengthLabel}</span>
        </>}
      </div>

      {ctxMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={closeCtxMenu} />
          <div
            data-testid={`clip-context-menu-${clip.id}`}
            className="fixed z-50 py-1"
            style={{ left: Math.max(8,Math.min(ctxMenu.x,window.innerWidth-240)), top: Math.max(8,Math.min(ctxMenu.y,window.innerHeight-480)), maxHeight:'80vh', overflowY:'auto', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', minWidth: isLibraryLoop ? 212 : 140 }}
          >
            {topItems.map(renderItem)}
            {isLibraryLoop && allowClipSound && (
              <div className="clip-sound-panel" data-testid={`clip-sound-panel-${clip.id}`}>
                <div className="clip-sound-row">
                  <span>Pitch</span>
                  <button data-testid="ctx-pitch-semi-down" aria-label="Down a semitone" title="Down a semitone (Shift + \u2193)" disabled={pitch <= -12} onClick={() => onClipSound?.(clip.id, { pitchDelta: -1 })}>{'\u2212'}</button>
                  <output data-testid="ctx-pitch-value" aria-label="Semitones">{pitchLabel(pitch)}</output>
                  <button data-testid="ctx-pitch-semi-up" aria-label="Up a semitone" title="Up a semitone (Shift + \u2191)" disabled={pitch >= 12} onClick={() => onClipSound?.(clip.id, { pitchDelta: 1 })}>+</button>
                </div>
                <div className="clip-sound-row is-choices">
                  <span>Octave</span>
                  <button data-testid="ctx-pitch-down" aria-pressed={pitch === -12} onClick={() => onClipSound?.(clip.id, { pitch: -12 })}>{'\u221212'}</button>
                  <button data-testid="ctx-pitch-reset" aria-pressed={pitch === 0} disabled={pitch === 0} onClick={() => onClipSound?.(clip.id, { pitch: 0 })}>0</button>
                  <button data-testid="ctx-pitch-up" aria-pressed={pitch === 12} onClick={() => onClipSound?.(clip.id, { pitch: 12 })}>+12</button>
                </div>
                <div className="clip-sound-row is-choices is-speed">
                  <span>Speed</span>
                  {SPEEDS.map((choice) => <button key={choice.id} data-testid={`ctx-speed-${choice.id}`} title={choice.title} aria-pressed={speed === choice.value} onClick={() => onClipSound?.(clip.id, { speed: choice.value })}>{choice.label}</button>)}
                </div>
              </div>
            )}
            {!clip.isPattern && editing.onClipEdit && <div className="clip-fade-settings">
              <label>Fade units<select aria-label="Fade units" value={fadeUnit} onChange={e=>setFadeUnit(e.target.value)}><option value="beats">Beats</option><option value="seconds">Seconds</option></select></label>
              {['in','out'].map(side=><label key={side}>Fade {side}<input aria-label={`Fade ${side} length`} type="number" min="0" step={fadeUnit==='beats'?.25:.05} key={`${side}-${fadeUnit}-${clip['fade_'+side]}`} defaultValue={Number(((side==='in'?fades.fadeIn:fades.fadeOut)*(fadeUnit==='beats'?sessionBpm/60:1)).toFixed(3))}
                onBlur={e=>{const n=Number(e.target.value);if(Number.isFinite(n)&&n>=0&&Math.abs(n-(side==='in'?fades.fadeIn:fades.fadeOut)*(fadeUnit==='beats'?sessionBpm/60:1))>0.0005)editing.onClipEdit(clip.id,{['fade_'+side]:n*(fadeUnit==='beats'?60/sessionBpm:1)});}} /></label>)}
              <button onClick={()=>{editing.onClipEdit(clip.id,{fade_in:0,fade_out:0});closeCtxMenu();}}>Reset fades</button>
            </div>}
            {bottomItems.map(renderItem)}
          </div>
        </>
      )}
    </>
  );
};

export default Clip;
